import { T, useGT } from 'gt-react';
import type {
  TaskTypeView,
  TaskTypeViewSourceKind,
  TaskTypeViewSourcePlacement,
} from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from 'src/components/ui/toggle-group';
import {
  listViewSourceOptions,
  moveViewPlacement,
  removeViewPlacement,
  setViewPlacementLabel,
  setViewSourceMode,
  setViewSourcePath,
  type ViewSchemaField,
} from './taskTypeViewModel';
import { ViewLabelInput, ViewPathSelect } from './TaskTypeViewPathControls';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from '@phosphor-icons/react';

type SourcePlacement = TaskTypeViewSourcePlacement;

export function TaskTypeViewSourceRow({
  view,
  regionId,
  placement,
  index,
  count,
  fields,
  fieldsReady,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  regionId: string;
  placement: SourcePlacement;
  index: number;
  count: number;
  fields: readonly ViewSchemaField[];
  fieldsReady: boolean;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  const gt = useGT();
  const sourceFields = fieldsReady
    ? listViewSourceOptions(fields, placement.component)
    : [];
  const source = placement.config.source;
  function commit(next: TaskTypeView) {
    if (next !== view) onCommit(next);
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-20 text-xs font-medium text-muted-foreground">
          {sourceKindLabel(placement.component, gt)}
        </span>
        <ToggleGroup
          type="single"
          value={source.from}
          disabled={disabled}
          aria-label={gt('Source', {
            context:
              'task page builder, where an email, document, AP invoice, or cash receipt viewer reads its id',
          })}
          onValueChange={(value) => {
            if (value !== 'anchor' && value !== 'path') return;
            commit(
              setViewSourceMode(view, regionId, placement.id, value, fields),
            );
          }}
        >
          <ToggleGroupItem value="anchor">
            <T context="task page builder, show the task anchor">Anchor</T>
          </ToggleGroupItem>
          <ToggleGroupItem value="path" disabled={sourceFields.length === 0}>
            <T context="task page builder, show an id stored on a field">
              Field
            </T>
          </ToggleGroupItem>
        </ToggleGroup>
        {source.from === 'path' ? (
          <ViewPathSelect
            options={sourceFields}
            path={source.path}
            disabled={disabled || !fieldsReady}
            onChange={(path) =>
              commit(setViewSourcePath(view, regionId, placement.id, path))
            }
          />
        ) : null}
        <ViewLabelInput
          label={placement.config.label}
          disabled={disabled}
          onChange={(label) =>
            commit(setViewPlacementLabel(view, regionId, placement.id, label))
          }
        />
        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled || index === 0}
            aria-label={gt('Move up', {
              context: 'task page builder, move an item up',
            })}
            onClick={() =>
              commit(moveViewPlacement(view, regionId, placement.id, -1))
            }
          >
            <ArrowUpIcon />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled || index >= count - 1}
            aria-label={gt('Move down', {
              context: 'task page builder, move an item down',
            })}
            onClick={() =>
              commit(moveViewPlacement(view, regionId, placement.id, 1))
            }
          >
            <ArrowDownIcon />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={gt('Remove', {
              context: 'task page builder, remove an item',
            })}
            onClick={() =>
              commit(removeViewPlacement(view, regionId, placement.id))
            }
          >
            <TrashIcon />
          </Button>
        </div>
      </div>
    </div>
  );
}

function sourceKindLabel(
  kind: TaskTypeViewSourceKind,
  gt: ReturnType<typeof useGT>,
): string {
  switch (kind) {
    case 'email':
      return gt('Email', { context: 'task page builder item kind, an email' });
    case 'document':
      return gt('Document', {
        context: 'task page builder item kind, a document',
      });
    case 'apInvoice':
      return gt('Invoice', {
        context: 'task page builder item kind, an AP invoice',
      });
    case 'cashReceipt':
      return gt('Cash Receipt', {
        context: 'task page builder item kind, a cash receipt',
      });
  }
}
